/**
 * 通用批量上传组件
 */
import { Component, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { PromotionApiService } from '@core/http/api/promotion.service';
import { StatesService } from '@core/store/states.service';
import { SharedModule } from '@shared/shared.module';
import { NzMessageService } from 'ng-zorro-antd/message';
import { forkJoin } from 'rxjs';
import * as XLSX from 'xlsx';
import { EmptyTableComponent } from '../empty-table/empty-table.component';
import { TableTotalComponent } from '../table-total/table-total.component';
import { getDemoFixture, hasDemoFixture } from './batch-upload-demo.fixture';

export interface BatchUploadConfig {
  /** 上传类型 */
  type: number;
  /** 模态框标题 */
  title: string;
  /** 模板下载链接 */
  templateUrl: string;
  /** 说明文本 */
  description: string;
  /** 上传时携带的额外数据 */
  uploadData?: () => Record<string, any>;
}

@Component({
  selector: 'app-batch-upload-modal',
  imports: [SharedModule, EmptyTableComponent, TableTotalComponent],
  templateUrl: './batch-upload-modal.component.html',
  styleUrl: './batch-upload-modal.component.less',
})
export class BatchUploadModalComponent {
  private message = inject(NzMessageService);
  private promotionApi = inject(PromotionApiService);
  private router = inject(Router);
  private statesService = inject(StatesService);

  // 输入属性
  readonly isVisible = input<boolean>(false);
  readonly config = input.required<BatchUploadConfig>();

  /**
   * 是不是演示环境（`DEMO_MODE=1` 那个实例）。
   * `environmentInfo$` 在 APP_INITIALIZER 里就填好了，早于页面渲染，这里直接读没问题。
   */
  get isDemo(): boolean {
    return this.statesService.environmentInfo$.getValue()?.demo === true;
  }

  /** 当前上传类型有没有备好的演示夹具 —— 有才显示演示区 */
  get showDemoEntry(): boolean {
    return hasDemoFixture(this.config().type);
  }

  // 输出事件
  readonly isVisibleChange = output<boolean>();
  readonly nzOnOk = output<void>();

  // 表格数据
  tableHeaders: string[] = [];
  tableData: any[] = [];
  showTable = false;
  errorMessages: string[] = [];
  originalListData: any[] = []; // 保存原始上传的数据，用于提交
  hasVerified = false; // 是否已经完成校验
  isSubmitting = false; // 是否正在提交
  isVerifying = false; // 是否正在校验

  // 分页相关
  pageIndex = 1;
  pageSize = 10;

  // 编辑状态管理
  editingCell: { row: any; header: string } | null = null;
  editingValue: string = '';
  hoveredCell: { row: any; header: string } | null = null;

  /**
   * 递归遍历并透传数据（保留表情等 Unicode 字符，不做过滤）
   */
  private sanitizeString(value: unknown): unknown {
    if (typeof value === 'string') {
      return value;
    }
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) {
        out[k] = this.sanitizeString(v);
      }
      return out;
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.sanitizeString(item));
    }
    return value;
  }

  /** 对表格行列表做字符串清理，供校验/提交使用 */
  private sanitizeListForApi<T extends Record<string, any>>(list: T[]): T[] {
    return list.map((row) => this.sanitizeString({ ...row }) as T);
  }

  // 从 URL 中提取模板文件名
  getTemplateName(): string {
    const url = this.config().templateUrl;
    if (!url) return '';
    const match = url.match(/\/([^\/]+\.(xlsx|xls|csv))$/i);
    return match ? match[1] : '';
  }

  // 计算表头宽度（根据文字长度）
  getHeaderWidth(header: string): string {
    const chineseChars = header.match(/[\u4e00-\u9fa5]/g)?.length || 0;
    const otherChars = header.length - chineseChars;
    const width = Math.max(120, chineseChars * 16 + otherChars * 8 + 40);
    return `${width}px`;
  }

  // 获取总数据条数
  get total(): number {
    return this.tableData.length;
  }

  // 获取当前页显示的数据
  get displayTableData(): any[] {
    const start = (this.pageIndex - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.tableData.slice(start, end);
  }

  // 分页变化处理
  onPageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.cancelEdit();
  }

  // 每页条数变化处理
  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.cancelEdit();
  }

  // 根据行数据获取原始索引
  getRowIndex(row: any): number {
    return this.tableData.findIndex((item) => item === row);
  }

  // 编辑相关方法
  startEdit(row: any, header: string): void {
    this.editingCell = { row, header };
    this.editingValue = row?.[header] || '';
  }

  saveEdit(): void {
    if (!this.editingCell) return;
    const { row, header } = this.editingCell;
    // 更新表格数据
    if (row) {
      row[header] = this.editingValue;
    }
    // 更新原始数据
    const rowIndex = this.getRowIndex(row);
    if (rowIndex >= 0 && this.originalListData[rowIndex]) {
      this.originalListData[rowIndex][header] = this.editingValue;
    }
    this.tableData = [...this.tableData];
    this.originalListData = [...this.originalListData];
    this.message.success('编辑成功');
    this.cancelEdit();
  }

  cancelEdit(): void {
    this.editingCell = null;
    this.editingValue = '';
  }

  isEditing(row: any, header: string): boolean {
    return this.editingCell?.row === row && this.editingCell?.header === header;
  }

  // 返回上一步
  goBack(): void {
    this.tableHeaders = [];
    this.tableData = [];
    this.showTable = false;
    this.errorMessages = [];
    this.originalListData = [];
    this.hasVerified = false;
    this.isSubmitting = false;
    this.isVerifying = false;
    this.editingCell = null;
    this.editingValue = '';
    this.pageIndex = 1;
    this.pageSize = 10;
  }

  onCancel(): void {
    this.goBack();
    this.isVisibleChange.emit(false);
  }

  // 处理文件上传
  handleFile(file: any): boolean {
    const isAllowed =
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls') ||
      file.name.endsWith('.csv');
    if (!isAllowed) {
      this.message.error('只能上传 Excel 或 CSV 文件格式');
      return false;
    }
    const reader = new FileReader();
    const isCSV = file.name.endsWith('.csv');
    reader.onload = (e: any) => {
      try {
        const binaryStr = e.target.result;
        let workbook: XLSX.WorkBook;
        if (isCSV) {
          workbook = XLSX.read(binaryStr, { type: 'binary', codepage: 65001 });
        } else {
          workbook = XLSX.read(binaryStr, { type: 'binary' });
        }
        const firstSheet = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheet];
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // 检查数据是否为空
        if (!jsonData || jsonData.length === 0) {
          this.message.remove();
          this.message.info('未识别到表格数据，请重新上传');
          return;
        }

        // 第一行作为表头
        const headers = jsonData[0] || [];
        if (!headers || headers.length === 0) {
          this.message.remove();
          this.message.info('未识别到表格表头，请重新上传');
          return;
        }

        // 处理表头：将 null/undefined 转换为空字符串，并去除首尾空格
        const processedHeaders: string[] = headers.map((header: any) =>
          header !== null && header !== undefined ? String(header).trim() : ''
        );

        // 获取有效表头（非空的表头）及其在原数组中的索引
        const validHeadersWithIndex: { header: string; index: number }[] = [];
        processedHeaders.forEach((header: string, index: number) => {
          if (header !== '') {
            validHeadersWithIndex.push({ header, index });
          }
        });

        if (validHeadersWithIndex.length === 0) {
          this.message.remove();
          this.message.info('未识别到有效的表格表头，请重新上传');
          return;
        }

        // 从第二行开始是数据
        const dataRows = jsonData.slice(1);

        // 将数据行转换为对象数组
        const listData = dataRows
          .filter((row: any) => row && Array.isArray(row))
          .map((row: any) => {
            const rowObj: Record<string, any> = {};
            validHeadersWithIndex.forEach(({ header, index }) => {
              const value = row[index];
              rowObj[header] = value !== null && value !== undefined ? value : '';
            });
            return rowObj;
          })
          .filter((row: any) => {
            // 过滤掉完全为空的行（所有字段都是空字符串）
            return Object.values(row).some((val: any) => val !== '');
          });

        if (listData.length === 0) {
          this.message.remove();
          this.message.info('未识别到表格中的数据，请重新上传');
          return;
        }

        this.applyParsedTable(
          validHeadersWithIndex.map(({ header }) => header),
          listData
        );
      } catch (error) {
        this.message.remove();
        this.message.error('表格解析失败，请检查文件格式');
      }
    };

    reader.onerror = () => {
      this.message.remove();
      this.message.error('文件读取失败，请重试');
    };

    reader.readAsArrayBuffer(file);
    // 阻止 nz-upload 自动上传
    return false;
  }

  /**
   * 把「已经解析出来的表头 + 数据行」铺到页面上，并走一次服务端校验。
   *
   * 两个入口共用：`handleFile()`（真人上传真文件）和 `loadDemoTable()`
   * （演示环境载入预置假数据）。口径完全一样 —— 演示看到的就是真实上传的那条链路。
   */
  private applyParsedTable(
    headers: string[],
    rows: Record<string, any>[]
  ): void {
    this.originalListData = this.sanitizeListForApi(rows);
    this.tableHeaders = headers;

    this.tableData = rows.map((row: Record<string, any>) => {
      const normalized: Record<string, any> = {};
      this.tableHeaders.forEach((header: string) => {
        const value = row[header];
        normalized[header] = value !== undefined && value !== null ? value : '';
      });
      return normalized;
    });

    this.pageIndex = 1;
    this.message.remove();

    this.showTable = true;
    // 铺开之后立刻校验，和上传真文件时一模一样
    this.verifyTableData();
  }

  /**
   * 演示环境专用：载入预置的假表格。
   *
   * 为什么需要它 —— 上传入口是原生文件选择框，智能体点不动 OS 弹窗，
   * 演示时这一步只能由真人代点。有了这个按钮，智能体自己就能把这幕走完。
   * 造表口径和数据来源见 `batch-upload-demo.fixture.ts`。
   */
  loadDemoTable(): void {
    const fixture = getDemoFixture(this.config().type);
    if (!fixture) {
      this.message.warning('这个上传类型没有备好演示表格');
      return;
    }
    // 拷一份再喂进去：页面上能直接编辑单元格，别把夹具本身改脏
    this.applyParsedTable(
      [...fixture.headers],
      fixture.rows.map((row) => ({ ...row }))
    );
  }

  // 提交数据
  handleSubmit(): void {
    if (!this.originalListData || this.originalListData.length === 0) {
      this.message.warning('没有可提交的数据');
      return;
    }
    // 先校验，校验成功后再提交
    this.verifyTableData(() => {
      this.isSubmitting = true;
      const BATCH_SIZE = 500; // 每批提交的数据条数
      const totalData = this.originalListData;
      const totalLength = totalData.length;

      // 如果数据量小于等于500条，直接提交
      if (totalLength <= BATCH_SIZE) {
        this.message.loading('正在提交...', { nzDuration: 0 });
        this.promotionApi
          .createUpdateTask({
            params: {
              list: this.sanitizeListForApi(totalData),
              type: this.config().type,
              finish: true, // 单批数据，直接完成
            },
            error: () => {
              this.isSubmitting = false;
              this.message.remove();
              this.message.error('提交失败');
            },
          })
          .subscribe(() => {
            this.isSubmitting = false;
            this.message.remove();
            this.isVisibleChange.emit(false);
            this.nzOnOk.emit();
            this.goBack();
            const currentUrl = this.router.url;
            const isOnTaskListPage = currentUrl === '/task-list';
            if (isOnTaskListPage) {
              this.message.success('上传成功，正在刷新任务列表…');
              setTimeout(() => {
                window.location.reload();
              }, 2000);
            } else {
              this.message.success('上传成功，正在跳转至任务列表…');
              setTimeout(() => {
                this.router.navigate(['/task-list']);
              }, 2000);
            }
          });
        return;
      }

      // 数据量超过500条，分批提交
      const batches: any[][] = [];
      for (let i = 0; i < totalLength; i += BATCH_SIZE) {
        batches.push(totalData.slice(i, i + BATCH_SIZE));
      }

      const loadingMessage = this.message.loading(
        `正在提交数据（共 ${totalLength} 条，分 ${batches.length} 批）...`,
        { nzDuration: 0 }
      );

      const submitBatch = (batchIndex: number, taskId: number | null): void => {
        if (batchIndex >= batches.length) {
          this.isSubmitting = false;
          this.message.remove(loadingMessage.messageId);
          this.isVisibleChange.emit(false);
          this.nzOnOk.emit();
          this.goBack();
          const currentUrl = this.router.url;
          const isOnTaskListPage = currentUrl === '/task-list';
          if (isOnTaskListPage) {
            this.message.success('上传成功，正在刷新任务列表…');
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          } else {
            this.message.success('上传成功，正在跳转至任务列表…');
            setTimeout(() => {
              this.router.navigate(['/task-list']);
            }, 2000);
          }
          return;
        }

        const batch = batches[batchIndex];
        const isLastBatch = batchIndex === batches.length - 1;
        const params: any = {
          list: this.sanitizeListForApi(batch),
          type: this.config().type,
        };

        // 如果不是第一批，传递 task_id
        if (taskId !== null) {
          params.task_id = taskId;
        }

        // 如果是最后一批，传递 finish: true
        if (isLastBatch) {
          params.finish = true;
        } else {
          params.finish = false;
        }

        this.promotionApi
          .createUpdateTask({
            params,
            error: () => {
              this.isSubmitting = false;
              this.message.remove(loadingMessage.messageId);
              this.message.error('提交失败');
            },
          })
          .subscribe({
            next: (response) => {
              let nextTaskId = taskId;
              if (response.data && response.data.task_id) {
                nextTaskId = response.data.task_id;
              }
              submitBatch(batchIndex + 1, nextTaskId);
            },
            error: () => {
              this.isSubmitting = false;
              this.message.remove(loadingMessage.messageId);
              this.message.error('提交失败，请重试');
            },
          });
      };

      submitBatch(0, null);
    });
  }

  // 重新校验
  verifyTableData(onSuccess?: () => void): void {
    this.hasVerified = false;
    this.isVerifying = true;
    const BATCH_SIZE = 500;
    const totalData = this.originalListData;
    const totalLength = totalData.length;

    // 如果数据量小于等于500条，直接校验
    if (totalLength <= BATCH_SIZE) {
      this.promotionApi
        .verifyTableData({
          params: {
            list: this.sanitizeListForApi(totalData),
            type: this.config().type,
          },
        })
        .subscribe({
          next: ({ data }) => {
            this.processVerifyResult(data, onSuccess);
          },
          error: () => {
            this.isVerifying = false;
            this.message.error('校验失败，请重试');
          },
        });
      return;
    }

    // 数据量超过500条，分批校验
    const batches: any[][] = [];
    for (let i = 0; i < totalLength; i += BATCH_SIZE) {
      batches.push(totalData.slice(i, i + BATCH_SIZE));
    }

    // 显示加载提示
    const loadingMessage = this.message.loading(
      `正在校验数据（共 ${totalLength} 条）...`,
      { nzDuration: 0 }
    );

    // 并发发送所有批次请求
    const batchRequests = batches.map((batch) =>
      this.promotionApi.verifyTableData({
        params: {
          list: this.sanitizeListForApi(batch),
          type: this.config().type,
        },
      })
    );

    forkJoin(batchRequests).subscribe({
      next: (responses) => {
        // 合并所有批次的结果
        const mergedErrorMessages: string[] = [];
        const mergedList: any[] = [];

        responses.forEach((response) => {
          const { data } = response;
          if (Array.isArray(data.error_message)) {
            mergedErrorMessages.push(...data.error_message);
          }
          if (Array.isArray(data.list)) {
            mergedList.push(...data.list);
          }
        });

        this.message.remove(loadingMessage.messageId);

        // 处理合并后的结果
        this.errorMessages = mergedErrorMessages;
        this.originalListData = mergedList;
        this.tableData = mergedList.map((row: Record<string, any>) => {
          const normalized: Record<string, any> = {};
          this.tableHeaders.forEach((header: string) => {
            const value = row?.[header];
            normalized[header] = value !== undefined && value !== null ? value : '';
          });
          return normalized;
        });
        // 重置分页
        this.pageIndex = 1;

        // 标记校验已完成
        this.hasVerified = true;
        this.isVerifying = false; // 校验完成

        // 如果有错误，显示提示
        if (this.errorMessages.length > 0) {
          this.message.warning(
            `校验失败，发现 ${this.errorMessages.length} 条错误，请修正后重新提交`
          );
        } else {
          this.message.success('表格校验完成');
          if (onSuccess) {
            onSuccess();
          }
        }
      },
      error: () => {
        this.isVerifying = false;
        this.hasVerified = true;
        this.message.remove(loadingMessage.messageId);
        this.message.error('校验失败，请重试');
      },
    });
  }

  // 处理校验结果
  private processVerifyResult(data: any, onSuccess?: () => void): void {
    this.errorMessages = Array.isArray(data.error_message) ? data.error_message : [];
    this.originalListData = Array.isArray(data.list) ? data.list : [];
    this.tableData = this.originalListData.map((row: Record<string, any>) => {
      const normalized: Record<string, any> = {};
      this.tableHeaders.forEach((header: string) => {
        const value = row?.[header];
        normalized[header] = value !== undefined && value !== null ? value : '';
      });
      return normalized;
    });
    // 重置分页
    this.pageIndex = 1;
    // 标记校验已完成
    this.hasVerified = true;
    this.isVerifying = false;

    // 如果有错误，显示提示
    if (this.errorMessages.length > 0) {
      this.message.warning(
        `校验失败，发现 ${this.errorMessages.length} 条错误，请修正后重新提交`
      );
    } else {
      this.message.success('表格校验完成');
      if (onSuccess) {
        onSuccess();
      }
    }
  }
}
